import SiteNav from '../components/layout/SiteNav'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../context/AuthContext'
import { User, Users, Shield, Clock, Compass, Calendar, Headphones, FolderOpen, Tag, Wrench } from 'lucide-react'

export default function Demo() {
  const navigate = useNavigate()
  const { signInDemo } = useAuthContext()

  function enterDemo(role, dest) {
    signInDemo(role)
    setTimeout(() => navigate(dest), 50)
  }

  const copper = '#B8734A'
  const sage = '#6B7D6B'
  const green = '#4A7C59'

  const cards = [
    { role:'employee', dest:'/dashboard', Icon:User,   iconColor:sage,   title:'Employee View',   name:'Jamie Mitchell', dept:'Operations',      desc:'Clock in/out, submit timesheets, request time off, read memos, complete onboarding, view documents and calendar.', btnColor:sage },
    { role:'supervisor', dest:'/admin',   Icon:Users,  iconColor:copper, title:'Supervisor View', name:'Sam Rivera',     dept:'Operations Dept', desc:'Approve time off and timesheets for your department, view team performance, manage department memos.',              btnColor:copper },
    { role:'hr_admin', dest:'/admin',     Icon:Shield, iconColor:green,  title:'HR Admin View',   name:'Kim Park',       dept:'Administrative',  desc:'Full HR access - all employees, approvals, performance, helpdesk, visitor log, memos, and documents.',             btnColor:green },
  ]

  const exploreItems = [
    { Icon:Clock,      color:copper, label:'Clock In/Out',       desc:"Live timer, job codes, today's time log" },
    { Icon:Compass,    color:sage,   label:'Digital Onboarding', desc:'W-4, I-9, Direct Deposit, e-signatures' },
    { Icon:Calendar,   color:copper, label:'Team Calendar',      desc:'Full CRUD, 9 event types, print support' },
    { Icon:Headphones, color:sage,   label:'Helpdesk',           desc:'IT & Facilities queues, 5-status workflow' },
    { Icon:FolderOpen, color:copper, label:'Document Library',   desc:'Upload, require signature, onboarding flow' },
    { Icon:Shield,     color:sage,   label:'Role System',        desc:'HR Admin, Supervisor, Employee tiers' },
    { Icon:Tag,        color:copper, label:'Visitor Kiosk',      desc:'NDA sign-in, badge assignment, live log' },
    { Icon:Wrench,     color:sage,   label:'Dev Requests',       desc:'Submit feature requests to GCW team' },
  ]

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: '#F7F2EB', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .demo-card { background: #fff; border: 1px solid #EDE6DA; border-radius: 16px; padding: 2rem; cursor: pointer; transition: all 0.25s; display: flex; flex-direction: column; }
        .demo-card:hover { transform: translateY(-6px); box-shadow: 0 12px 40px rgba(43,43,43,0.1); }
        .primary-btn { background: #B8734A; color: #fff; border: none; border-radius: 8px; padding: 12px 28px; font-family: Montserrat, sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .primary-btn:hover { background: #C9895F; transform: translateY(-1px); }
      `}</style>

      <SiteNav />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '5rem 2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#B8734A', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.75rem' }}>Live Demo - No Login Required</div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '3rem', fontWeight: 700, color: '#2B2B2B', marginBottom: '1rem', lineHeight: 1.15 }}>See it for yourself.</h1>
          <p style={{ fontSize: 15, color: '#6B7D6B', lineHeight: 1.8, maxWidth: 520, margin: '0 auto' }}>
            Three roles. One click. No password needed. Experience exactly what each type of user sees in Collective Staff.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: '2.5rem' }}>
          {cards.map((card) => (
            <div key={card.role} className="demo-card" style={{ borderTop: `3px solid ${card.btnColor}` }} onClick={() => enterDemo(card.role, card.dest)}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: card.iconColor + '15', border: `1px solid ${card.iconColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <card.Icon size={26} color={card.iconColor} strokeWidth={1.75} />
              </div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 600, color: '#2B2B2B', marginBottom: 4 }}>{card.title}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: card.btnColor, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{card.name}</div>
              <div style={{ fontSize: 11, color: '#8A9E8A', marginBottom: '1rem' }}>{card.dept}</div>
              <div style={{ fontSize: 13, color: '#6B7D6B', lineHeight: 1.7, marginBottom: '1.5rem', flex: 1 }}>{card.desc}</div>
              <div style={{ background: card.btnColor, color: '#fff', borderRadius: 8, padding: '12px', fontSize: 13, fontWeight: 600, textAlign: 'center', marginTop: 'auto' }}>
                Enter {card.title}
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: '#2B2B2B', borderRadius: 14, padding: '1.75rem 2rem', marginBottom: '2.5rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#B8734A', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '1.25rem', textAlign: 'center' }}>What you can explore in the demo</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
            {exploreItems.map(({ Icon, color, label, desc }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 6, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Icon size={15} color={color} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 10, color: 'rgba(217,195,163,0.45)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 14, padding: '1.5rem 2rem', marginBottom: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(184,115,74,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Tag size={22} color="#B8734A" strokeWidth={1.75} />
            </div>
            <div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 600, color: '#2B2B2B', marginBottom: 3 }}>Try the Visitor Kiosk</div>
              <div style={{ fontSize: 13, color: '#6B7D6B' }}>Sign in as a guest, read the visitor NDA, and get a digital badge - just like a real visitor would.</div>
            </div>
          </div>
          <button className="primary-btn" onClick={() => navigate('/visitor')}>Open Visitor Kiosk</button>
        </div>

        <div style={{ background: '#2B2B2B', borderRadius: 16, padding: '2.5rem', textAlign: 'center' }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>Want a personalized walkthrough?</h3>
          <p style={{ fontSize: 13, color: 'rgba(217,195,163,0.5)', lineHeight: 1.8, marginBottom: '1.5rem', maxWidth: 480, margin: '0 auto 1.5rem' }}>
            Schedule a call with Tiffany - we will set up a demo portal with YOUR branding on the call and walk through every feature together.
          </p>
          <button className="primary-btn" style={{ fontSize: 14, padding: '12px 32px' }} onClick={() => navigate('/about')}>Schedule a Demo</button>
        </div>
      </div>
    </div>
  )
}